// /Users/donovan68/dva-frontend-vite/src/components/ProProgressPathV3.tsx
import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { PersonalizationWidgetCompactV9 } from './PersonalizationWidgetCompactV9';

interface ProProgressPathV3Props {
  currentPhase: string; 
  onPhaseClick?: (phaseId: string) => void;
  personalizationData?: {
    strategy?: number;
    capabilities?: number;
    readiness?: number;
    docsCount?: number;
  };
  onPersonalizationClick?: () => void;
}

// Updated to 3-step structure per new journey simplification
const PRO_STEPS = [
  { 
    id: 'portfolio', 
    label: 'Portfolio Exploration',
    number: 1,
    description: 'Discover and prioritize analytics opportunities.',
    zone: 'planning'
  },
  { 
    id: 'analysis', 
    label: 'Analysis & Qualification',
    number: 2,
    description: 'Drill down, model ROI, and qualify initiatives.',
    zone: 'planning'
  },
  { 
    id: 'approval_execution', 
    label: 'Approval & Execution',
    number: 3,
    description: 'Align roadmap, secure approval, and move to execution.',
    zone: 'execution'
  }
];

/**
 * Helper function to map current route to step ID
 * This allows parent components to determine active step based on URL
 */
export function getPhaseFromRoute(pathname: string): string {
  // Portfolio Exploration (Step 1)
  if (pathname.includes('/snapshot') || 
      pathname.includes('/matrix') || 
      pathname.includes('/portfolio')) {
    return 'portfolio';
  }
  
  // Analysis & Qualification (Step 2)
  if (pathname.includes('/usecase/') || 
      pathname.includes('/roi') || 
      pathname.includes('/analysis')) {
    return 'analysis';
  }
  
  // Approval & Execution (Step 3) - includes roadmap as a supporting tool
  if (pathname.includes('/roadmap') ||
      pathname.includes('/approval') || 
      pathname.includes('/execution') || 
      pathname.includes('/resources') ||
      pathname.includes('/strategic')) {
    return 'approval_execution';
  }
  
  // Default to portfolio
  return 'portfolio';
}

/**
 * Helper to get step completion status
 * Returns: 'completed', 'active', or 'upcoming'
 */
function getPhaseStatus(phase: typeof PRO_STEPS[0], currentPhase: string): 'completed' | 'active' | 'upcoming' {
  const phaseIndex = PRO_STEPS.findIndex(p => p.id === phase.id);
  const currentIndex = PRO_STEPS.findIndex(p => p.id === currentPhase);
  
  if (phaseIndex < currentIndex) return 'completed';
  if (phaseIndex === currentIndex) return 'active';
  return 'upcoming';
}

export function ProProgressPathV3({ 
  currentPhase, 
  onPhaseClick, 
  personalizationData,
  onPersonalizationClick
}: ProProgressPathV3Props) {
  const [hoveredPhase, setHoveredPhase] = useState<string | null>(null);

  return (
    <div className="flex items-stretch gap-4 justify-end">
      {/* Main Journey Bar - expands to fill space */}
      <div className="flex-1 bg-white rounded-xl shadow-md border border-[#e5e7eb]">
        <div className="px-4 pt-2 pb-3">
          {/* Pro Features Badge */}
          <div className="flex items-center justify-end mb-1">
            <span className="text-xs px-2 py-0.5 bg-[#FF6E4C]/10 text-[#FF6E4C] rounded-full" style={{ fontWeight: 600 }}>
              Pro Features Unlocked
            </span>
          </div>
          
          {/* Journey Steps (3 steps instead of 4 phases) */}
          <div className="flex items-center justify-between">
            <TooltipProvider delayDuration={100}>
              {PRO_STEPS.map((phase, index) => {
                const status = getPhaseStatus(phase, currentPhase);
                
                return (
                  <React.Fragment key={phase.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className="flex flex-col items-center flex-1 group cursor-pointer"
                          onMouseEnter={() => setHoveredPhase(phase.id)}
                          onMouseLeave={() => setHoveredPhase(null)}
                          onClick={() => onPhaseClick?.(phase.id)}
                        >
                          <div className="relative">
                            {/* Step circle */}
                            <div
                              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                                status === 'completed'
                                  ? 'bg-[#18365E] text-white'
                                  : status === 'active'
                                  ? 'bg-[#FF6E4C] text-white shadow-lg'
                                  : 'bg-white text-[#18365E] border-2 border-[#18365E]/40'
                              } ${hoveredPhase === phase.id ? 'scale-110 shadow-xl' : ''}`}
                              style={{ fontWeight: 700 }}
                            >
                              {status === 'completed' ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : (
                                phase.number
                              )}
                            </div>
                            
                            {/* Active step glow animation */}
                            {status === 'active' && (
                              <div className="absolute inset-0 rounded-full bg-[#FF6E4C]/30 animate-ping" />
                            )}
                          </div>
                          
                          {/* Step label */}
                          <span
                            className={`mt-1.5 text-xs text-center max-w-[120px] transition-all ${
                              status === 'active' 
                                ? 'text-[#0A2540]' 
                                : 'text-[#18365E]'
                            }`}
                            style={{ fontWeight: status === 'active' ? 700 : 600 }}
                          >
                            {phase.label}
                          </span>
                          
                          {/* Active step underline glow */}
                          {status === 'active' && (
                            <div className="mt-1 h-0.5 w-16 bg-gradient-to-r from-transparent via-[#FF6E4C] to-transparent animate-pulse" />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-[#0A2540] text-white border-0 max-w-xs">
                        <p className="text-xs" style={{ fontWeight: 500 }}>{phase.description}</p>
                      </TooltipContent>
                    </Tooltip>
                    
                    {/* Connector line between steps */}
                    {index < PRO_STEPS.length - 1 && (
                      <div
                        className={`h-0.5 flex-1 mx-1.5 mt-[-42px] transition-all duration-500 ${
                          status === 'completed' 
                            ? 'bg-[#18365E]'
                            : 'bg-[#e5e7eb]'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </TooltipProvider>
          </div>
          
          {/* Updated Zone labels for 3-step structure */}
          <div className="flex items-center justify-center gap-3 mt-3 pt-2 border-t border-[#e5e7eb]">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#18365E]" />
              <span className="text-[10px] text-[#717182]" style={{ fontWeight: 600 }}>
                Strategic Planning (Steps 1–2)
              </span>
            </div>
            
            <div className="w-px h-3 bg-[#e5e7eb]" />
            
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#FF6E4C]" />
              <span className="text-[10px] text-[#717182]" style={{ fontWeight: 600 }}>
                Execution & Activation (Step 3)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Personalization Widget - fixed width, right-aligned, height-matched */}
      <div className="hidden lg:block shrink-0 w-[240px]">
        <PersonalizationWidgetCompactV9 
          personalizationData={personalizationData}
          onContinueClick={onPersonalizationClick}
          stepContext={currentPhase as 'portfolio' | 'analysis' | 'approval_execution'}
        />
      </div>
    </div>
  );
}